// src/features/system/orchestrator/components/ScopeCleanerPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Scope-level data cleaner — namespace-driven UI for purging scopes,
// outcomes, runs, hot-stream data and GCS artifacts.
//
// Reuses the same NamespaceList + checkbox selection pattern from
// ScopeMigrationPanel (DRY). Wraps the existing cleanup & GCS delete APIs.
//
// Flow:
//   1. Browse & multi-select scope namespaces on the left panel.
//   2. Configure purge targets via checkboxes on the right panel.
//   3. Hit Preview (dry-run) → counts are shown per target.
//   4. Hit Execute → mandatory "im sure" typed confirmation in a dialog.
// ────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Trash2,
  Eye,
  Check,
  Minus,
  AlertTriangle,
  Loader2,
  ShieldAlert,
  Database,
  HardDrive,
  Flame,
  FileText,
} from 'lucide-react';

import {
  fetchWorkflowScopesApi,
  fetchScopeNamespacesApi,
  type WorkflowScope,
  type ScopeNamespaceCount,
} from '../api/scopesApi';
import { useTenantOverride } from '@/shared/auth/tenantContext';
import {
  workflowDataCleanupApi,
  gcsDeleteApi,
  type WorkflowDataCleanupTargets,
  type WorkflowDataCleanupCounts,
  type GcsDeleteResponse,
} from '../api/cleanupApi';
import NamespaceList, { SearchInput } from '@/shared/components/ui/NamespaceList';
import AppDialog from '@/shared/components/ui/AppDialog';

// ── Constants ────────────────────────────────────────────────────────────────
const SCOPE_KEY_LIMIT = 500;

// ── Cleanup target flags ─────────────────────────────────────────────────────
type CleanupTarget = {
  scopes: boolean;
  outcomes: boolean;
  runs: boolean;
  steps: boolean;
  executions: boolean;
  run_outcomes: boolean;
  gcs_files: boolean;
};

const DEFAULT_TARGETS: CleanupTarget = {
  scopes: false,
  outcomes: true,
  runs: false,
  steps: false,
  executions: false,
  run_outcomes: false,
  gcs_files: false,
};

// ── Aggregated preview result ────────────────────────────────────────────────
type AggregatedPreview = {
  namespaces: string[];
  matched: WorkflowDataCleanupCounts;
  gcs_uris: string[];
  per_namespace: Record<string, WorkflowDataCleanupCounts>;
};

type ExecuteResult = {
  deleted: WorkflowDataCleanupCounts;
  gcs: GcsDeleteResponse | null;
  per_namespace: Record<string, WorkflowDataCleanupCounts>;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function _ns(s: WorkflowScope): string {
  return s.scope_namespace ?? (s as any).scopeNamespace ?? '';
}

function countLabel(n: number): string {
  return n.toLocaleString();
}

function sumCounts(a: WorkflowDataCleanupCounts, b: WorkflowDataCleanupCounts): WorkflowDataCleanupCounts {
  return {
    runs: a.runs + b.runs,
    steps: a.steps + b.steps,
    executions: a.executions + b.executions,
    outcomes: a.outcomes + b.outcomes,
    run_outcomes: a.run_outcomes + b.run_outcomes,
    scopes: a.scopes + b.scopes,
  };
}

const ZERO_COUNTS: WorkflowDataCleanupCounts = { runs: 0, steps: 0, executions: 0, outcomes: 0, run_outcomes: 0, scopes: 0 };

// ── Target descriptor array — drives the checkbox grid + preview cards ───────
const TARGET_DESCRIPTORS: Array<{
  key: keyof CleanupTarget;
  label: string;
  blurb: string;
  icon: React.ElementType;
  tone: string;
  implies?: (keyof CleanupTarget)[];
}> = [
  {
    key: 'outcomes',
    label: 'Outcomes',
    blurb: 'Step outcome events linked to matching scopes.',
    icon: FileText,
    tone: 'text-amber-400',
  },
  {
    key: 'run_outcomes',
    label: 'Run Outcomes',
    blurb: 'Run-level outcome aggregations.',
    icon: FileText,
    tone: 'text-amber-400',
  },
  {
    key: 'executions',
    label: 'Executions',
    blurb: 'Workflow step execution records.',
    icon: Database,
    tone: 'text-orange-400',
    implies: ['outcomes'],
  },
  {
    key: 'steps',
    label: 'Steps',
    blurb: 'Workflow step rows.',
    icon: Database,
    tone: 'text-orange-400',
    implies: ['executions', 'outcomes'],
  },
  {
    key: 'runs',
    label: 'Runs',
    blurb: 'Workflow run rows + cascaded children.',
    icon: Database,
    tone: 'text-rose-400',
    implies: ['steps', 'executions', 'outcomes', 'run_outcomes'],
  },
  {
    key: 'scopes',
    label: 'Scopes',
    blurb: 'Scope entity rows. Requires Runs target.',
    icon: Database,
    tone: 'text-rose-500',
    implies: ['runs', 'steps', 'executions', 'outcomes', 'run_outcomes'],
  },
  {
    key: 'gcs_files',
    label: 'GCS Files',
    blurb: 'Cloud Storage artifacts referenced by outcomes.',
    icon: HardDrive,
    tone: 'text-red-400',
  },
];

// ── Main Component ───────────────────────────────────────────────────────────

export default function ScopeCleanerPanel() {
  /* ── Centralized tenant context (sidebar TenantSelector is single source of truth) ── */
  const { tenants: tenantList, activeTenantId: ctxTenantId } = useTenantOverride();
  const activeTenantId: number | '' = ctxTenantId ?? '';
  const activeTid = activeTenantId !== '' ? activeTenantId : undefined;

  /* ── Namespace selection state (mirrors ScopeMigrationPanel) ── */
  const [selectedNs, setSelectedNs] = useState<string | null>(null);
  const [nsSearch, setNsSearch] = useState('');
  const [keySearch, setKeySearch] = useState('');
  const [selectedScopeIds, setSelectedScopeIds] = useState<Set<number>>(new Set());
  const [loadedNsScopes, setLoadedNsScopes] = useState<Map<string, number[]>>(new Map());
  const [loadingNs, setLoadingNs] = useState<Set<string>>(new Set());

  const queryClient = useQueryClient();

  /* ── Cleanup targets ─────────────────────────────────────────── */
  const [targets, setTargets] = useState<CleanupTarget>({ ...DEFAULT_TARGETS });

  /* ── Preview / execute state ─────────────────────────────────── */
  const [preview, setPreview] = useState<AggregatedPreview | null>(null);
  const [executeResult, setExecuteResult] = useState<ExecuteResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  /* ── Namespaces data ─────────────────────────────────────────── */
  const { data: namespacesRaw = [], isLoading: nsLoading } = useQuery<ScopeNamespaceCount[]>({
    queryKey: ['cleaner', 'scopeNamespaces', activeTid],
    queryFn: async () => {
      const res = await fetchScopeNamespacesApi(activeTid);
      return (res?.items ?? []) as ScopeNamespaceCount[];
    },
    staleTime: 30_000,
  });

  const namespaces = useMemo(() => {
    const term = nsSearch.trim().toLowerCase();
    if (!term) return namespacesRaw;
    return namespacesRaw.filter((n) => n.scope_namespace.toLowerCase().includes(term));
  }, [namespacesRaw, nsSearch]);

  const totalScopeCount = useMemo(
    () => namespacesRaw.reduce((sum, n) => sum + n.count, 0),
    [namespacesRaw],
  );

  /* ── Scope keys for the selected namespace ──────────────────── */
  const { data: scopeKeysRaw = [], isLoading: keysLoading } = useQuery<WorkflowScope[]>({
    queryKey: ['cleaner', 'scopeKeys', selectedNs, activeTid],
    queryFn: async () => {
      if (!selectedNs) return [];
      const res = await fetchWorkflowScopesApi({ scope_namespace: selectedNs, limit: SCOPE_KEY_LIMIT, tenant_id: activeTid });
      return (res?.items ?? res ?? []) as WorkflowScope[];
    },
    enabled: !!selectedNs,
    staleTime: 30_000,
  });

  /* Cache scope IDs per namespace as they load */
  useEffect(() => {
    if (!selectedNs || scopeKeysRaw.length === 0) return;
    const ids = scopeKeysRaw.map((s) => s.id_scope!).filter((id) => id != null);
    setLoadedNsScopes((prev) => {
      const existing = prev.get(selectedNs);
      if (existing && existing.length === ids.length && existing.every((v, i) => v === ids[i])) return prev;
      const next = new Map(prev);
      next.set(selectedNs, ids);
      return next;
    });
  }, [selectedNs, scopeKeysRaw]);

  const filteredScopes = useMemo(() => {
    const term = keySearch.trim().toLowerCase();
    if (!term) return scopeKeysRaw;
    return scopeKeysRaw.filter((s) => {
      const raw = (s.scope_key ?? '').toLowerCase();
      return raw.includes(term);
    });
  }, [scopeKeysRaw, keySearch]);

  /* ── Multi-select helpers (same DRY pattern as migration) ──── */

  function toggleScopeId(id: number) {
    setSelectedScopeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAllScopes() {
    const allIds = filteredScopes.map((s) => s.id_scope!).filter((id) => id != null);
    setSelectedScopeIds((prev) => {
      const next = new Set(prev);
      const allSelected = allIds.length > 0 && allIds.every((id) => next.has(id));
      if (allSelected) allIds.forEach((id) => next.delete(id));
      else allIds.forEach((id) => next.add(id));
      return next;
    });
  }

  async function toggleNamespaceScopes(ns: string) {
    let ids = loadedNsScopes.get(ns);
    if (!ids) {
      setLoadingNs((prev) => { const n = new Set(prev); n.add(ns); return n; });
      try {
        const res = await fetchWorkflowScopesApi({ scope_namespace: ns, limit: SCOPE_KEY_LIMIT, tenant_id: activeTid });
        const scopes = (res?.items ?? res ?? []) as WorkflowScope[];
        ids = scopes.map((s: WorkflowScope) => s.id_scope!).filter((id: number) => id != null);
        setLoadedNsScopes((prev) => { const next = new Map(prev); next.set(ns, ids!); return next; });
      } catch { return; } finally {
        setLoadingNs((prev) => { const n = new Set(prev); n.delete(ns); return n; });
      }
    }
    if (ids.length === 0) return;
    setSelectedScopeIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids!.every((id) => next.has(id));
      if (allSelected) ids!.forEach((id) => next.delete(id));
      else ids!.forEach((id) => next.add(id));
      return next;
    });
  }

  async function toggleAllNamespaces() {
    const visibleNs = namespaces.map((n) => n.scope_namespace);
    const cached = new Map(loadedNsScopes);
    const toFetch = visibleNs.filter((ns) => !cached.has(ns));
    if (toFetch.length > 0) {
      setLoadingNs(new Set(toFetch));
      try {
        const results = await Promise.allSettled(
          toFetch.map(async (ns) => {
            const res = await fetchWorkflowScopesApi({ scope_namespace: ns, limit: SCOPE_KEY_LIMIT, tenant_id: activeTid });
            const scopes = (res?.items ?? res ?? []) as WorkflowScope[];
            return { ns, ids: scopes.map((s: WorkflowScope) => s.id_scope!).filter((id: number) => id != null) };
          }),
        );
        for (const r of results) {
          if (r.status === 'fulfilled') cached.set(r.value.ns, r.value.ids);
        }
        setLoadedNsScopes(new Map(cached));
      } finally { setLoadingNs(new Set()); }
    }
    const visibleIds: number[] = [];
    for (const ns of visibleNs) {
      const ids = cached.get(ns);
      if (ids) visibleIds.push(...ids);
    }
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedScopeIds.has(id));
    setSelectedScopeIds((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  /* ── Tri-state selection indicators ─────────────────────────── */

  const allFilteredSelected = filteredScopes.length > 0 &&
    filteredScopes.every((s) => s.id_scope != null && selectedScopeIds.has(s.id_scope));

  const someFilteredSelected = !allFilteredSelected && filteredScopes.length > 0 &&
    filteredScopes.some((s) => s.id_scope != null && selectedScopeIds.has(s.id_scope));

  const nsSelectionState = useMemo(() => {
    const map = new Map<string, 'all' | 'partial' | 'none'>();
    const entries = Array.from(loadedNsScopes.entries());
    for (const [ns, ids] of entries) {
      if (ids.length === 0) { map.set(ns, 'none'); continue; }
      const sel = ids.filter((id: number) => selectedScopeIds.has(id)).length;
      map.set(ns, sel === 0 ? 'none' : sel === ids.length ? 'all' : 'partial');
    }
    return map;
  }, [loadedNsScopes, selectedScopeIds]);

  const allNsSelectState = useMemo((): 'all' | 'partial' | 'none' => {
    if (namespaces.length === 0) return 'none';
    const loaded = namespaces.filter((n) => loadedNsScopes.has(n.scope_namespace));
    if (loaded.length === 0 && selectedScopeIds.size === 0) return 'none';
    const allAll = loaded.length === namespaces.length &&
      loaded.every((n) => nsSelectionState.get(n.scope_namespace) === 'all');
    if (allAll) return 'all';
    const anyVisible = loaded.some((n) => nsSelectionState.get(n.scope_namespace) !== 'none');
    return anyVisible ? 'partial' : selectedScopeIds.size > 0 ? 'partial' : 'none';
  }, [namespaces, loadedNsScopes, nsSelectionState, selectedScopeIds.size]);

  /* ── Derive selected namespaces for the cleanup calls ───────── */
  const selectedNamespaces = useMemo(() => {
    const nsSet = new Set<string>();
    const entries = Array.from(loadedNsScopes.entries());
    for (const [ns, ids] of entries) {
      if (ids.some((id: number) => selectedScopeIds.has(id))) nsSet.add(ns);
    }
    return Array.from(nsSet).sort();
  }, [loadedNsScopes, selectedScopeIds]);

  /* ── Reset selection when tenant changes via sidebar ──────── */
  useEffect(() => {
    setSelectedNs(null);
    setSelectedScopeIds(new Set());
    setLoadedNsScopes(new Map());
    setPreview(null);
    setExecuteResult(null);
  }, [activeTenantId]);

  /* ── Target toggle helper ───────────────────────────────────── */
  function toggleTarget(key: keyof CleanupTarget) {
    setTargets((prev) => {
      const next = { ...prev };
      const willEnable = !next[key];
      next[key] = willEnable;
      // If enabling a target with implications, auto-enable them.
      if (willEnable) {
        const desc = TARGET_DESCRIPTORS.find((d) => d.key === key);
        if (desc?.implies) {
          for (const dep of desc.implies) next[dep] = true;
        }
      }
      // If disabling 'runs', must also disable 'scopes' (scopes requires runs).
      if (!willEnable && key === 'runs') next.scopes = false;
      return next;
    });
    // Clear stale preview on target change.
    setPreview(null);
    setExecuteResult(null);
  }

  const anyTargetActive = TARGET_DESCRIPTORS.some((d) => targets[d.key]);

  /* ── Build the WorkflowDataCleanupTargets from our CleanupTarget ─ */
  function toApiTargets(): WorkflowDataCleanupTargets {
    return {
      scopes: targets.scopes,
      outcomes: targets.outcomes,
      runs: targets.runs,
      steps: targets.steps,
      executions: targets.executions,
      run_outcomes: targets.run_outcomes,
    };
  }

  /* ── Preview mutation (dry-run per selected namespace) ──────── */
  const previewMutation = useMutation({
    mutationFn: async (): Promise<AggregatedPreview> => {
      if (selectedNamespaces.length === 0) throw new Error('No namespaces selected.');
      if (!anyTargetActive) throw new Error('No cleanup targets selected.');

      const apiTargets = toApiTargets();
      const perNs: Record<string, WorkflowDataCleanupCounts> = {};
      let aggregated = { ...ZERO_COUNTS };
      let allGcs: string[] = [];

      // Parallel dry-run per namespace for isolated counts.
      const results = await Promise.allSettled(
        selectedNamespaces.map(async (ns) => {
          const res = await workflowDataCleanupApi({
            dry_run: true,
            filters: { scope_namespace: ns },
            targets: apiTargets,
            ...(activeTid ? { tenant_id: activeTid } : {}),
          });
          return { ns, res };
        }),
      );

      for (const r of results) {
        if (r.status === 'fulfilled') {
          const { ns, res } = r.value;
          perNs[ns] = res.matched;
          aggregated = sumCounts(aggregated, res.matched);
          if (res.gcs_uris) allGcs = allGcs.concat(res.gcs_uris);
        }
      }

      return { namespaces: selectedNamespaces, matched: aggregated, gcs_uris: allGcs, per_namespace: perNs };
    },
    onSuccess: (data) => {
      setPreview(data);
      setExecuteResult(null);
    },
  });

  /* ── Execute mutation (real delete per namespace + GCS) ─────── */
  const executeMutation = useMutation({
    mutationFn: async (): Promise<ExecuteResult> => {
      if (!preview) throw new Error('Preview first.');

      const apiTargets = toApiTargets();
      const perNs: Record<string, WorkflowDataCleanupCounts> = {};
      let aggregated = { ...ZERO_COUNTS };

      // Sequential execution for safety (avoid overwhelming the backend).
      for (const ns of preview.namespaces) {
        const res = await workflowDataCleanupApi({
          dry_run: false,
          filters: { scope_namespace: ns },
          targets: apiTargets,
          ...(activeTid ? { tenant_id: activeTid } : {}),
        });
        perNs[ns] = res.deleted;
        aggregated = sumCounts(aggregated, res.deleted);
      }

      // GCS file cleanup (if selected and URIs were found).
      let gcsResult: GcsDeleteResponse | null = null;
      if (targets.gcs_files && preview.gcs_uris.length > 0) {
        // Batch into chunks of 500 (API limit).
        const allDeleted: string[] = [];
        const allFailed: { uri: string; error: string }[] = [];
        const allSkipped: string[] = [];
        for (let i = 0; i < preview.gcs_uris.length; i += 500) {
          const batch = preview.gcs_uris.slice(i, i + 500);
          const res = await gcsDeleteApi(batch);
          allDeleted.push(...(res.deleted ?? []));
          allFailed.push(...(res.failed ?? []));
          allSkipped.push(...(res.skipped ?? []));
        }
        gcsResult = { ok: true, deleted: allDeleted, failed: allFailed, skipped: allSkipped };
      }

      return { deleted: aggregated, gcs: gcsResult, per_namespace: perNs };
    },
    onSuccess: (data) => {
      setExecuteResult(data);
      setConfirmOpen(false);
      setConfirmText('');
      setPreview(null);

      // Reset local selection state — those scopes no longer exist.
      setSelectedScopeIds(new Set());
      setLoadedNsScopes(new Map());

      // Invalidate all scope-related query caches across the app
      // so every panel reflects the purged data without a page refresh.
      queryClient.invalidateQueries({ queryKey: ['cleaner', 'scopeNamespaces'] });
      queryClient.invalidateQueries({ queryKey: ['cleaner', 'scopeKeys'] });
      queryClient.invalidateQueries({ queryKey: ['migrate', 'scopeNamespaces'] });
      queryClient.invalidateQueries({ queryKey: ['migrate', 'scopeKeys'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'scopeNamespaces'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'scopeKeys'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'workflowScopes'] });
      queryClient.invalidateQueries({ queryKey: ['scopes'] });
    },
  });

  const isConfirmValid = confirmText.trim().toLowerCase() === 'im sure';
  const isBusy = previewMutation.isPending || executeMutation.isPending;

  /* ══════════════════════════════════════════════════════════════ */
  /* ── Render ────────────────────────────────────────────────── */
  /* ══════════════════════════════════════════════════════════════ */

  return (
    <div className="h-full min-h-0 flex flex-col gap-3 overflow-hidden">
      {/* ── Confirm execution dialog ────────────────────────────── */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Confirm destructive cleanup"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-md border border-rose-800/40 bg-rose-950/20">
            <ShieldAlert className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
            <div className="space-y-1 text-sm">
              <p className="text-rose-300 font-medium">This action is irreversible.</p>
              <p className="text-neutral-400 text-xs">
                You are about to permanently delete workflow data across{' '}
                <span className="text-rose-300 font-medium">{preview?.namespaces.length ?? 0}</span>{' '}
                namespace{(preview?.namespaces.length ?? 0) !== 1 ? 's' : ''}
                {preview && preview.matched.scopes > 0 && (
                  <> — totalling{' '}
                    <span className="text-rose-300 font-medium">{countLabel(preview.matched.scopes)}</span>{' '}
                    scope{preview.matched.scopes !== 1 ? 's' : ''}
                  </>
                )}.
              </p>
            </div>
          </div>

          {/* ── Per-namespace breakdown ──────────────────────────── */}
          {preview && Object.keys(preview.per_namespace).length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-medium">Namespaces targeted</div>
              <div className="max-h-36 overflow-y-auto space-y-0.5 rounded border border-neutral-800/60 pac-glass p-1.5">
                {Object.entries(preview.per_namespace)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([ns, counts]) => (
                    <div
                      key={ns}
                      className="flex items-center gap-2 text-[11px] px-2 py-1 rounded hover:border-neutral-600 transition-colors"
                    >
                      <Flame className="w-3 h-3 text-rose-500/60 shrink-0" />
                      <span className="flex-1 min-w-0 truncate text-neutral-300 font-mono">{ns}</span>
                      <div className="flex items-center gap-2 shrink-0 tabular-nums">
                        {counts.scopes > 0 && (
                          <span className="text-rose-400">{countLabel(counts.scopes)} <span className="text-neutral-600">sc</span></span>
                        )}
                        {counts.runs > 0 && (
                          <span className="text-rose-400/70">{countLabel(counts.runs)} <span className="text-neutral-600">runs</span></span>
                        )}
                        {counts.outcomes > 0 && (
                          <span className="text-amber-400/70">{countLabel(counts.outcomes)} <span className="text-neutral-600">out</span></span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* ── Aggregate totals ────────────────────────────────── */}
          {preview && (
            <div className="grid grid-cols-3 gap-2 text-xs">
              {preview.matched.scopes > 0 && (
                <div className="pac-glass rounded px-2 py-1.5 border border-neutral-800">
                  <span className="text-neutral-500">Scopes</span>
                  <span className="ml-1 text-rose-400 font-medium">{countLabel(preview.matched.scopes)}</span>
                </div>
              )}
              {preview.matched.outcomes > 0 && (
                <div className="pac-glass rounded px-2 py-1.5 border border-neutral-800">
                  <span className="text-neutral-500">Outcomes</span>
                  <span className="ml-1 text-amber-400 font-medium">{countLabel(preview.matched.outcomes)}</span>
                </div>
              )}
              {preview.matched.runs > 0 && (
                <div className="pac-glass rounded px-2 py-1.5 border border-neutral-800">
                  <span className="text-neutral-500">Runs</span>
                  <span className="ml-1 text-rose-400 font-medium">{countLabel(preview.matched.runs)}</span>
                </div>
              )}
              {preview.matched.steps > 0 && (
                <div className="pac-glass rounded px-2 py-1.5 border border-neutral-800">
                  <span className="text-neutral-500">Steps</span>
                  <span className="ml-1 text-orange-400 font-medium">{countLabel(preview.matched.steps)}</span>
                </div>
              )}
              {preview.matched.executions > 0 && (
                <div className="pac-glass rounded px-2 py-1.5 border border-neutral-800">
                  <span className="text-neutral-500">Executions</span>
                  <span className="ml-1 text-orange-400 font-medium">{countLabel(preview.matched.executions)}</span>
                </div>
              )}
              {preview.gcs_uris.length > 0 && targets.gcs_files && (
                <div className="pac-glass rounded px-2 py-1.5 border border-neutral-800">
                  <span className="text-neutral-500">GCS files</span>
                  <span className="ml-1 text-red-400 font-medium">{countLabel(preview.gcs_uris.length)}</span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs text-neutral-400">
              Type{' '}
              <span className="font-mono text-rose-400 font-bold">im sure</span>{' '}
              to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="im sure"
              autoFocus
              className="w-full pac-glass/60 rounded px-3 py-2 text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:border-rose-600 transition-colors font-mono"
            />
          </div>

          {executeMutation.isError && (
            <p className="text-xs text-rose-400">
              Error: {String((executeMutation.error as Error)?.message || executeMutation.error)}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              className="px-3 py-1.5 rounded border border-neutral-700 text-neutral-300 hover:border-neutral-500 text-sm"
              onClick={() => { setConfirmOpen(false); setConfirmText(''); }}
              disabled={executeMutation.isPending}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1.5 rounded border border-rose-700/60 bg-rose-900/30 text-rose-300 hover:bg-rose-800/40 hover:border-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              disabled={!isConfirmValid || executeMutation.isPending}
              onClick={() => executeMutation.mutate()}
            >
              {executeMutation.isPending ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Purging…</>
              ) : (
                <><Trash2 className="w-3.5 h-3.5" /> Execute Cleanup</>
              )}
            </button>
          </div>
        </div>
      </AppDialog>

      {/* ── Main split layout ────────────────────────────────────── */}
      <div className="flex-1 min-h-0 grid grid-cols-[1fr_1fr] gap-3 overflow-hidden">

        {/* ══════════ LEFT: Source scope selection ══════════════════ */}
        <div className="min-h-0 flex flex-col border border-neutral-800 rounded-md overflow-hidden relative">
          <div className="px-3 py-2 border-b border-neutral-800 pac-glass-subtle flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-200">Source Scopes</span>
              {activeTenantId !== '' && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-900/40 text-indigo-300 border border-indigo-700/40">
                  🏢 {tenantList.find(t => t.idTenant === activeTenantId)?.tenantName ?? `#${activeTenantId}`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedScopeIds.size > 0 && (
                <>
                  <span className="text-[10px] text-emerald-400 tabular-nums">
                    {selectedScopeIds.size} / {totalScopeCount.toLocaleString()} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedScopeIds(new Set()); setPreview(null); setExecuteResult(null); }}
                    className="text-[10px] text-neutral-500 hover:text-rose-400 transition-colors"
                  >
                    Clear
                  </button>
                  <span className="text-neutral-700">·</span>
                </>
              )}
              <span className="text-[10px] text-neutral-500">{namespacesRaw.length} namespaces</span>
            </div>
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-[180px_1fr] overflow-hidden">
            {/* ── Namespace sidebar ────────────────────────────── */}
            <NamespaceList
              namespaces={namespaces}
              selectedNamespace={selectedNs}
              onSelect={(ns) => { setSelectedNs(ns); setKeySearch(''); }}
              selectionState={nsSelectionState}
              onToggleSelection={toggleNamespaceScopes}
              selectAllState={allNsSelectState}
              onSelectAll={toggleAllNamespaces}
              loadingNamespaces={loadingNs}
              searchValue={nsSearch}
              onSearchChange={setNsSearch}
              loading={nsLoading}
            />

            {/* ── Scope keys list ─────────────────────────────── */}
            <div className="min-h-0 flex flex-col overflow-hidden">
              {selectedNs && (
                <div className="px-2 py-1.5 border-b border-neutral-800/50 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={toggleAllScopes}
                    className="shrink-0 group"
                    title={allFilteredSelected ? 'Deselect all' : 'Select all'}
                  >
                    {allFilteredSelected ? (
                      <span className="w-3.5 h-3.5 rounded-[3px] flex items-center justify-center bg-emerald-600 border border-emerald-500 group-hover:bg-emerald-500 transition-all">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    ) : someFilteredSelected ? (
                      <span className="w-3.5 h-3.5 rounded-[3px] flex items-center justify-center bg-amber-600/80 border border-amber-500 group-hover:bg-amber-500 transition-all">
                        <Minus className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    ) : (
                      <span className="w-3.5 h-3.5 rounded-[3px] inline-block border border-neutral-600 group-hover:border-neutral-400 transition-colors" />
                    )}
                  </button>
                  <SearchInput value={keySearch} onChange={setKeySearch} placeholder="Filter keys…" />
                  <span className="text-[10px] text-neutral-500 shrink-0 tabular-nums">
                    {selectedScopeIds.size > 0 && (
                      <span className="text-emerald-400 mr-1">{filteredScopes.filter((s) => s.id_scope != null && selectedScopeIds.has(s.id_scope)).length}/</span>
                    )}
                    {filteredScopes.length}
                  </span>
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {!selectedNs ? (
                  <div className="h-full flex items-center justify-center">
                    <span className="text-[11px] text-neutral-500">Select a namespace</span>
                  </div>
                ) : keysLoading ? (
                  <div className="h-full flex items-center justify-center">
                    <span className="text-[11px] text-neutral-400">Loading…</span>
                  </div>
                ) : filteredScopes.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <span className="text-[11px] text-neutral-500">
                      {keySearch ? 'No matching keys.' : 'Empty namespace.'}
                    </span>
                  </div>
                ) : (
                  filteredScopes.map((s, idx) => {
                    const id = s.id_scope;
                    const isSelected = id != null && selectedScopeIds.has(id);
                    const raw = s.scope_key ?? '';

                    return (
                      <button
                        key={`${_ns(s)}::${raw}::${idx}`}
                        type="button"
                        onClick={() => id != null && toggleScopeId(id)}
                        className={
                          'w-full text-left px-2 py-1.5 text-[11px] border-b border-neutral-800/30 ' +
                          'flex items-center gap-2 transition-colors cursor-default group ' +
                          (isSelected
                            ? 'bg-emerald-950/40 text-emerald-200 border-l-2 border-l-emerald-500'
                            : 'text-neutral-300 hover:bg-black')
                        }
                      >
                        {/* checkbox */}
                        {isSelected ? (
                          <span className="shrink-0 w-3.5 h-3.5 rounded-[3px] flex items-center justify-center bg-emerald-600 border border-emerald-500">
                            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                          </span>
                        ) : (
                          <span className="shrink-0 w-3.5 h-3.5 rounded-[3px] border border-neutral-600 group-hover:border-neutral-400 transition-colors" />
                        )}
                        <span className="truncate flex-1 min-w-0 font-mono">{raw}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════ RIGHT: Cleanup configuration ════════════════ */}
        <div className="min-h-0 flex flex-col border border-neutral-800 rounded-md overflow-hidden">
          <div className="px-3 py-2 border-b border-neutral-800 pac-glass-subtle flex items-center justify-between shrink-0">
            <span className="text-sm font-medium text-neutral-200">Cleanup Configuration</span>
            {selectedNamespaces.length > 0 && (
              <span className="text-[10px] text-amber-400 tabular-nums">
                {selectedNamespaces.length} namespace{selectedNamespaces.length !== 1 ? 's' : ''} targeted
              </span>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">

            {/* ── Target checkboxes grid ────────────────────────── */}
            <div className="space-y-2">
              <div className="text-xs text-neutral-400 font-medium uppercase tracking-wider">Purge Targets</div>
              <div className="grid grid-cols-2 gap-2">
                {TARGET_DESCRIPTORS.map((desc) => {
                  const Icon = desc.icon;
                  const active = targets[desc.key];
                  // Disable scopes if runs isn't selected (backend requirement).
                  const disabled = desc.key === 'scopes' && !targets.runs;

                  return (
                    <button
                      key={desc.key}
                      type="button"
                      onClick={() => !disabled && toggleTarget(desc.key)}
                      className={
                        'text-left px-3 py-2 rounded-md border transition-all flex items-start gap-2.5 ' +
                        (disabled
                          ? 'opacity-30 cursor-not-allowed border-neutral-800 pac-glass'
                          : active
                            ? 'border-rose-700/50 bg-rose-950/20 hover:border-rose-600'
                            : 'border-neutral-800 pac-glass hover:border-neutral-600 hover:border-neutral-600')
                      }
                    >
                      <div className="shrink-0 mt-0.5">
                        {active ? (
                          <span className="w-3.5 h-3.5 rounded-[3px] flex items-center justify-center bg-rose-600 border border-rose-500">
                            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                          </span>
                        ) : (
                          <span className="w-3.5 h-3.5 rounded-[3px] border border-neutral-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <Icon className={`w-3 h-3 ${active ? desc.tone : 'text-neutral-500'}`} />
                          <span className={`text-xs font-medium ${active ? 'text-neutral-200' : 'text-neutral-400'}`}>
                            {desc.label}
                          </span>
                        </div>
                        <p className="text-[10px] text-neutral-500 mt-0.5 leading-tight">{desc.blurb}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Action buttons ────────────────────────────────── */}
            <div className="flex items-center gap-2">
              <button
                className="px-4 py-1.5 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                disabled={selectedNamespaces.length === 0 || !anyTargetActive || isBusy}
                onClick={() => previewMutation.mutate()}
              >
                {previewMutation.isPending ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning…</>
                ) : (
                  <><Eye className="w-3.5 h-3.5" /> Preview</>
                )}
              </button>

              {preview && (
                <button
                  className="px-4 py-1.5 rounded border border-rose-700/60 bg-rose-900/30 text-rose-300 hover:bg-rose-800/40 hover:border-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                  disabled={isBusy}
                  onClick={() => { setConfirmText(''); setConfirmOpen(true); }}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Execute Cleanup
                </button>
              )}
            </div>

            {/* ── Validation messages ──────────────────────────── */}
            {selectedNamespaces.length === 0 && selectedScopeIds.size === 0 && (
              <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" /> Select scope namespaces on the left panel to target.
              </div>
            )}
            {!anyTargetActive && selectedNamespaces.length > 0 && (
              <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" /> Enable at least one purge target above.
              </div>
            )}

            {previewMutation.isError && (
              <div className="text-xs text-rose-400 p-2 border border-rose-800/40 rounded bg-rose-950/20">
                Preview failed: {String((previewMutation.error as Error)?.message || previewMutation.error)}
              </div>
            )}

            {/* ── Preview results ───────────────────────────────── */}
            {preview && (
              <div className="space-y-3">
                <div className="text-xs text-neutral-400 font-medium uppercase tracking-wider flex items-center gap-1.5">
                  <Eye className="w-3 h-3" /> Preview — Dry Run
                </div>

                {/* aggregate counts */}
                <div className="grid grid-cols-3 gap-2">
                  {([
                    ['Scopes', preview.matched.scopes, 'text-rose-400'] as const,
                    ['Outcomes', preview.matched.outcomes, 'text-amber-400'] as const,
                    ['Run Outcomes', preview.matched.run_outcomes, 'text-amber-400'] as const,
                    ['Runs', preview.matched.runs, 'text-rose-400'] as const,
                    ['Steps', preview.matched.steps, 'text-orange-400'] as const,
                    ['Executions', preview.matched.executions, 'text-orange-400'] as const,
                  ] as const).filter(([, v]) => v > 0).map(([label, count, tone]) => (
                    <div key={label} className="pac-glass rounded-md px-3 py-2 border border-neutral-800">
                      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
                      <div className={`text-lg font-semibold tabular-nums ${tone}`}>{countLabel(count)}</div>
                    </div>
                  ))}
                  {targets.gcs_files && preview.gcs_uris.length > 0 && (
                    <div className="pac-glass rounded-md px-3 py-2 border border-neutral-800">
                      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">GCS Files</div>
                      <div className="text-lg font-semibold tabular-nums text-red-400">{countLabel(preview.gcs_uris.length)}</div>
                    </div>
                  )}
                </div>

                {/* Zero-match notice */}
                {preview.matched.scopes === 0 && preview.matched.outcomes === 0 && preview.matched.runs === 0 &&
                 preview.matched.steps === 0 && preview.matched.executions === 0 && preview.gcs_uris.length === 0 && (
                  <div className="text-xs text-neutral-500 p-2 border border-neutral-800 rounded">
                    No matching data found for the selected namespaces and targets.
                  </div>
                )}

                {/* Per-namespace breakdown (collapsed by default) */}
                {Object.keys(preview.per_namespace).length > 1 && (
                  <details className="group">
                    <summary className="text-[10px] text-neutral-500 cursor-pointer hover:text-neutral-300 transition-colors">
                      Per-namespace breakdown ({Object.keys(preview.per_namespace).length} namespaces)
                    </summary>
                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {Object.entries(preview.per_namespace)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([ns, counts]) => {
                          const total = counts.scopes + counts.outcomes + counts.runs + counts.steps + counts.executions + counts.run_outcomes;
                          return (
                            <div key={ns} className="flex items-center gap-2 text-[11px] px-2 py-1 pac-glass rounded/50">
                              <span className="flex-1 min-w-0 truncate text-neutral-300 font-mono">{ns}</span>
                              <span className="text-neutral-500 tabular-nums shrink-0">{countLabel(total)} rows</span>
                            </div>
                          );
                        })}
                    </div>
                  </details>
                )}

                {/* GCS URIs preview */}
                {targets.gcs_files && preview.gcs_uris.length > 0 && (
                  <details className="group">
                    <summary className="text-[10px] text-neutral-500 cursor-pointer hover:text-neutral-300 transition-colors">
                      GCS files to delete ({preview.gcs_uris.length})
                    </summary>
                    <div className="mt-2 max-h-32 overflow-y-auto space-y-0.5">
                      {preview.gcs_uris.slice(0, 100).map((uri, i) => (
                        <div key={i} className="text-[10px] text-neutral-400 font-mono truncate px-2 py-0.5 pac-glass rounded">
                          {uri}
                        </div>
                      ))}
                      {preview.gcs_uris.length > 100 && (
                        <div className="text-[10px] text-neutral-600 px-2">… and {preview.gcs_uris.length - 100} more</div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            )}

            {/* ── Execution results ─────────────────────────────── */}
            {executeResult && (
              <div className="space-y-3">
                <div className="text-xs text-emerald-400 font-medium uppercase tracking-wider flex items-center gap-1.5">
                  <Check className="w-3 h-3" /> Cleanup Complete
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {([
                    ['Scopes', executeResult.deleted.scopes, 'text-emerald-400'] as const,
                    ['Outcomes', executeResult.deleted.outcomes, 'text-emerald-400'] as const,
                    ['Run Outcomes', executeResult.deleted.run_outcomes, 'text-emerald-400'] as const,
                    ['Runs', executeResult.deleted.runs, 'text-emerald-400'] as const,
                    ['Steps', executeResult.deleted.steps, 'text-emerald-400'] as const,
                    ['Executions', executeResult.deleted.executions, 'text-emerald-400'] as const,
                  ] as const).filter(([, v]) => v > 0).map(([label, count, tone]) => (
                    <div key={label} className="pac-glass rounded-md px-3 py-2 border border-emerald-800/30">
                      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
                      <div className={`text-lg font-semibold tabular-nums ${tone}`}>{countLabel(count)}</div>
                    </div>
                  ))}
                  {executeResult.gcs && (
                    <div className="pac-glass rounded-md px-3 py-2 border border-emerald-800/30">
                      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">GCS Deleted</div>
                      <div className="text-lg font-semibold tabular-nums text-emerald-400">{countLabel(executeResult.gcs.deleted.length)}</div>
                      {executeResult.gcs.failed.length > 0 && (
                        <div className="text-[10px] text-rose-400 mt-0.5">{executeResult.gcs.failed.length} failed</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Per-namespace breakdown */}
                {Object.keys(executeResult.per_namespace).length > 1 && (
                  <details className="group">
                    <summary className="text-[10px] text-neutral-500 cursor-pointer hover:text-neutral-300 transition-colors">
                      Per-namespace results ({Object.keys(executeResult.per_namespace).length} namespaces)
                    </summary>
                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {Object.entries(executeResult.per_namespace)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([ns, counts]) => {
                          const total = counts.scopes + counts.outcomes + counts.runs + counts.steps + counts.executions + counts.run_outcomes;
                          return (
                            <div key={ns} className="flex items-center gap-2 text-[11px] px-2 py-1 pac-glass rounded border border-emerald-800/20">
                              <span className="flex-1 min-w-0 truncate text-neutral-300 font-mono">{ns}</span>
                              <span className="text-emerald-400 tabular-nums shrink-0">{countLabel(total)} deleted</span>
                            </div>
                          );
                        })}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
